import { toError } from '@shared/helpers/errors.helper';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  isPermanentNotFoundMessage,
  isTransientResourceLoadError,
} from './resource-load.helpers';

/** Retry user hydration while the API is down (local branch switch / server restart). */
export const TOKEN_HYDRATION_RETRY_MS = 3_000;

export type TokenRefreshInFlightLock = {
  /**
   * True when this call owns the in-flight refresh and must call
   * {@link TokenRefreshInFlightLock.end}.
   */
  tryStart: () => boolean;
  /** Release the lock so another refresh can start. */
  end: () => void;
};

/**
 * Single-flight lock so overlapping token refreshes cannot dispatch after a
 * later success and clear the expiry timer.
 */
export const createTokenRefreshInFlightLock = (): TokenRefreshInFlightLock => {
  let inFlight = false;
  return {
    tryStart: () => {
      if (inFlight) return false;
      inFlight = true;
      return true;
    },
    end: () => {
      inFlight = false;
    },
  };
};

/**
 * Milliseconds between hydration retries, or null to disable the interval.
 */
export const getTokenHydrationRetryIntervalMs = (options: {
  auth0IsLoading: boolean;
  hasExpiry: boolean;
  shouldRetryHydration: boolean;
}): number | null => {
  if (options.auth0IsLoading || options.hasExpiry) return null;
  if (!options.shouldRetryHydration) return null;
  return TOKEN_HYDRATION_RETRY_MS;
};

/** Auth0 codes that mean the session is gone — retrying would hide the login redirect. */
const AUTH_SESSION_END_CODES = new Set([
  'login_required',
  'consent_required',
  'invalid_grant',
  'missing_refresh_token',
]);

/**
 * True when token refresh must load the app user because organizations are
 * not in memory yet.
 */
export const shouldFetchUserForTokenRefresh = (
  userId: string | undefined,
  organizationCount: number,
): userId is string =>
  guards.is.nonEmptyString(userId) && organizationCount === 0;

/**
 * Auth0 `error` field when present, otherwise {@link Error.message}.
 */
const getAuthSessionEndCode = (error: Error): string | undefined => {
  const code = Reflect.get(error, 'error');
  if (guards.is.string(code) && AUTH_SESSION_END_CODES.has(code)) return code;
  if (AUTH_SESSION_END_CODES.has(error.message)) return error.message;
  return undefined;
};

/**
 * True when initial hydration should keep retrying (API down, 5xx, 429).
 * False for Auth0 session-end, membership/org not-found, and other
 * non-transient HTTP statuses so the loader can exit.
 */
export const isRetryableTokenHydrationError = (error: unknown): boolean => {
  const err = toError(error);
  if (getAuthSessionEndCode(err) !== undefined) return false;
  if (isPermanentNotFoundMessage(err.message)) return false;
  return isTransientResourceLoadError(err);
};
